import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MasterModalContext from 'contexts/MasterModalContext'
import {
  get as getLocalStorage,

  set as setLocalStorage,
} from 'lib/storage/isomorphicLocalStorage'
import { matchPath } from 'react-router'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { LOYALTY_DISMISS_STAY_UPGRADE_PROMO_MODAL_STORAGE_KEY } from 'loyalty/constants/loyaltyConfig'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltyStayUpgradePromoBenefitModal from 'loyalty/components/StayUpgradePromo/LoyaltyStayUpgradePromoBenefitModal'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import useLoyaltyUserBenefitView from '../useLoyaltyUserBenefitView'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'

const pathsBlacklist = [
  '/:region/checkout',
  '/:region/booking/confirmation',
  `/:region/${LOYALTY_LANDING_PAGE}`,
  '/:region/offer',
]

function useLoyaltyStayUpgradePromoModal() {
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const loyaltyUpgradesPromo = useLabFeatureFlag(LabFeatureFlags.enableLoyaltyUpgradesPromo)
  const modalState = useContext(MasterModalContext)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account)
  const [tier] = useCurrentLoyaltyTier()
  const hasDismissedModal = getLocalStorage(LOYALTY_DISMISS_STAY_UPGRADE_PROMO_MODAL_STORAGE_KEY)
  const upgradeBenefit = useLoyaltyUserBenefitView('hotel_upgrade')

  const path = useAppSelector(state => state.router.location.pathname)

  const isModalPreventedPage = !!matchPath(path, {
    path: pathsBlacklist,
    exact: false,
  })

  const shouldShowModal = useMemo(() => (
    loggedIn &&
    loyaltyEnabled &&
    loyaltyUpgradesPromo?.enabled &&
    !hasDismissedModal &&
    !modalState.open &&
    hasLoyaltyAccount &&
    !loyaltyAccount.fetching &&
    !loyaltyAccount.error &&
    !isModalPreventedPage &&
    (tier === 'silver' || tier === 'gold') &&
    ((upgradeBenefit?.availability ?? 0) > 0)
  ), [loggedIn, loyaltyEnabled, loyaltyUpgradesPromo?.enabled, hasDismissedModal, modalState.open, hasLoyaltyAccount, tier, loyaltyAccount.fetching, loyaltyAccount.error, isModalPreventedPage, upgradeBenefit?.availability])

  const showStayUpgradePromoModal = useCallback(() => {
    showModal<boolean>(<LoyaltyStayUpgradePromoBenefitModal />)
    setLocalStorage(LOYALTY_DISMISS_STAY_UPGRADE_PROMO_MODAL_STORAGE_KEY, true)
  }, [showModal])

  useEffect(() => {
    if (shouldShowModal) showStayUpgradePromoModal()
  }, [shouldShowModal, showStayUpgradePromoModal])
}

export default useLoyaltyStayUpgradePromoModal
